import { Navigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useFiscalStore } from "@/store/fiscal-store";
import { fmtBRL, fmtNum } from "@/lib/format";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from "recharts";
import { TrendingUp, Percent, Info, Receipt, Layers, ArrowDownUp, PieChart as PieIcon, BarChart3 } from "lucide-react";

const TRIBUTOS_CONFIG = [
  { sigla: "ICMS",    label: "ICMS",     cor: "#1A66CC", desc: "Imposto sobre Circulação de Mercadorias e Serviços" },
  { sigla: "ICMS-ST", label: "ICMS-ST",  cor: "#33CCFF", desc: "Substituição Tributária do ICMS" },
  { sigla: "FCP",     label: "FCP",      cor: "#6366F1", desc: "Fundo de Combate à Pobreza" },
  { sigla: "FCP-ST",  label: "FCP-ST",   cor: "#818CF8", desc: "FCP na Substituição Tributária" },
  { sigla: "IPI",     label: "IPI",      cor: "#10B981", desc: "Imposto sobre Produtos Industrializados" },
  { sigla: "PIS",     label: "PIS",      cor: "#F59E0B", desc: "Programa de Integração Social" },
  { sigla: "COFINS",  label: "COFINS",   cor: "#EC4899", desc: "Contribuição para o Financiamento da Seguridade Social" },
  { sigla: "IBS",     label: "IBS",      cor: "#0EA5E9", desc: "Imposto sobre Bens e Serviços (Reforma Tributária)" },
  { sigla: "CBS",     label: "CBS",      cor: "#14B8A6", desc: "Contribuição sobre Bens e Serviços (Reforma Tributária)" },
  { sigla: "ISSQN",   label: "ISSQN",    cor: "#F97316", desc: "Imposto Sobre Serviços de Qualquer Natureza" },
];

type SortKey = "sigla" | "base" | "valor" | "aliq";

export default function TributacaoPage() {
  const { notas, dashboard } = useFiscalStore();
  const [sortKey, setSortKey] = useState<SortKey>("valor");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  useEffect(() => { document.title = "Tributação — Extrator Fiscal"; }, []);

  const consolidado = useMemo(() => {
    const acc: Record<string, { base: number; valor: number; count: number }> = {};
    for (const nota of notas) {
      for (const imp of nota.impostosDetalhe ?? []) {
        if (!acc[imp.sigla]) acc[imp.sigla] = { base: 0, valor: 0, count: 0 };
        acc[imp.sigla].base += imp.base;
        acc[imp.sigla].valor += imp.valor;
        acc[imp.sigla].count += 1;
      }
    }
    return acc;
  }, [notas]);

  const totalTributos = Object.values(consolidado).reduce((s, v) => s + v.valor, 0);
  const totalBase = Object.values(consolidado).reduce((s, v) => s + v.base, 0);
  const totalReceita = dashboard?.stats.totalRevenue || 0;

  const pieData = TRIBUTOS_CONFIG
    .map((t) => ({ name: t.label, value: consolidado[t.sigla]?.valor ?? 0, cor: t.cor }))
    .filter((d) => d.value > 0)
    .sort((a, b) => b.value - a.value);

  const barData = TRIBUTOS_CONFIG
    .map((t) => ({ name: t.sigla, base: consolidado[t.sigla]?.base ?? 0, valor: consolidado[t.sigla]?.valor ?? 0 }))
    .filter((d) => d.valor > 0)
    .sort((a, b) => b.valor - a.valor);

  const aliquotaEfetiva = totalBase > 0 ? (totalTributos / totalBase) * 100 : 0;
  const cargaFiscal = totalReceita > 0 ? (totalTributos / totalReceita) * 100 : 0;

  const tableData = useMemo(() => {
    return TRIBUTOS_CONFIG
      .map(t => {
        const dados = consolidado[t.sigla] || { base: 0, valor: 0 };
        const aliq = dados.base > 0 ? (dados.valor / dados.base) * 100 : 0;
        const pct = totalTributos > 0 ? (dados.valor / totalTributos) * 100 : 0;
        return { ...t, ...dados, aliq, pct };
      })
      .filter(t => t.valor > 0)
      .sort((a, b) => {
        const factor = sortDir === "asc" ? 1 : -1;
        if (sortKey === "sigla") return a.sigla.localeCompare(b.sigla) * factor;
        return (a[sortKey] - b[sortKey]) * factor;
      });
  }, [consolidado, sortKey, sortDir, totalTributos]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  };

  if (!dashboard) return <Navigate to="/upload" replace />;

  return (
    <div className="px-8 py-8 max-w-[1600px] mx-auto space-y-8 animate-in fade-in duration-500">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight dark:text-white">Tributação Consolidada</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Cálculo de carga tributária e distribuição técnica baseada nas NFs processadas.
          </p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-xl">
          <Receipt className="size-4 text-blue-600 dark:text-blue-400" />
          <span className="text-sm font-medium text-blue-900 dark:text-blue-200">{fmtNum(Object.keys(consolidado).length)} Tributos Identificados</span>
        </div>
      </header>

      {/* KPIs com Estilo Glass */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Levantamento de Tributos" value={fmtBRL(totalTributos)} sub="Consolidado de Impostos" icon={Layers} color="text-primary" />
        <KpiCard label="Soma de Bases de Cálculo" value={fmtBRL(totalBase)} sub="Composição base" icon={Receipt} color="text-amber-500" />
        <KpiCard label="Alíquota Média Ponderada" value={`${aliquotaEfetiva.toFixed(2)}%`} sub="Sobre base total" icon={TrendingUp} color="text-red-500" />
        <KpiCard label="Carga Fiscal Estimada" value={`${cargaFiscal.toFixed(2)}%`} sub="Sobre Faturamento" icon={Percent} color="text-emerald-500" />
      </div>

      {/* Gráficos em Grid */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-bold flex items-center gap-2 text-lg">
              <PieIcon className="size-5 text-primary" /> Distribuição da Carga por Tributo
            </h2>
          </div>
          {pieData.length > 0 ? (
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={70} outerRadius={100} paddingAngle={4} stroke="none">
                    {pieData.map((d, i) => <Cell key={i} fill={d.cor} />)}
                  </Pie>
                  <Tooltip 
                    formatter={(v: any) => fmtBRL(v)} 
                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                  />
                  <Legend verticalAlign="bottom" height={36} iconType="circle"/>
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <EmptyState message="Nenhum tributo para exibir." />
          )}
        </div>

        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-bold flex items-center gap-2 text-lg">
              <BarChart3 className="size-5 text-primary" /> Comparativo: Base de Cálculo vs Valor
            </h2>
            <span className="text-[10px] uppercase font-bold text-muted-foreground bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">
              Visão Detalhada
            </span>
          </div>
          {barData.length > 0 ? (
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData} margin={{ left: 0, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" className="opacity-10" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11 }} tickFormatter={v => `R$${v/1000}k`} />
                  <Tooltip 
                    formatter={(v: any) => fmtBRL(v)}
                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                  />
                  <Legend verticalAlign="top" iconType="circle" wrapperStyle={{ paddingBottom: '20px' }} />
                  <Bar dataKey="base" name="Base de Cálculo" fill="#1A66CC" radius={[4, 4, 0, 0]} barSize={40} />
                  <Bar dataKey="valor" name="Valor do Tributo" fill="#EF4444" radius={[4, 4, 0, 0]} barSize={40} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <EmptyState message="Dados insuficientes." />
          )}
        </div>
      </div>

      {/* Tabela de Detalhamento com Inteligência */}
      <div className="glass-card overflow-hidden">
        <div className="p-6 border-b border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <h2 className="font-bold text-lg flex items-center gap-2">
            <Layers className="size-5 text-primary" /> Quadro de Detalhamento Técnico
          </h2>
          <div className="group relative">
            <Info className="size-4 text-muted-foreground cursor-help" />
            <div className="absolute right-0 bottom-full mb-2 w-72 p-3 bg-slate-900 text-white text-[10px] rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-xl">
              A alíquota efetiva é calculada pela divisão direta entre o Valor Total e a Base de Cálculo, permitindo identificar distorções causadas por benefícios fiscais ou isenções.
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead className="bg-slate-50/5 dark:bg-slate-900/50">
              <tr className="text-muted-foreground">
                <th 
                  className="text-left p-4 font-bold text-[10px] uppercase cursor-pointer hover:text-primary group/th"
                  onClick={() => toggleSort("sigla")}
                >
                  <div className="flex items-center gap-1">Sigla <ArrowDownUp className="size-3 opacity-30 group-hover/th:opacity-100" /></div>
                </th>
                <th className="text-left p-4 font-bold text-[10px] uppercase">Descrição</th>
                <th 
                  className="text-right p-4 font-bold text-[10px] uppercase cursor-pointer hover:text-primary group/th"
                  onClick={() => toggleSort("base")}
                >
                  <div className="flex items-center justify-end gap-1">Base de Cálculo <ArrowDownUp className="size-3 opacity-30 group-hover/th:opacity-100" /></div>
                </th>
                <th 
                  className="text-right p-4 font-bold text-[10px] uppercase cursor-pointer hover:text-primary group/th text-amber-500"
                  onClick={() => toggleSort("aliq")}
                >
                  <div className="flex items-center justify-end gap-1">Aliq. Efetiva <ArrowDownUp className="size-3 opacity-30 group-hover/th:opacity-100" /></div>
                </th>
                <th 
                  className="text-right p-4 font-bold text-[10px] uppercase cursor-pointer hover:text-primary group/th text-emerald-500"
                  onClick={() => toggleSort("valor")}
                >
                  <div className="flex items-center justify-end gap-1">Valor Total <ArrowDownUp className="size-3 opacity-30 group-hover/th:opacity-100" /></div>
                </th>
                <th className="text-right p-4 font-bold text-[10px] uppercase">Partic. %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {tableData.map((t) => (
                <tr key={t.sigla} className="hover:bg-primary/5 transition-colors group">
                  <td className="p-4">
                    <span className="font-extrabold text-[10px] px-2 py-1 rounded-md border" 
                      style={{ background: `${t.cor}15`, color: t.cor, borderColor: `${t.cor}30` }}>
                      {t.sigla}
                    </span>
                  </td>
                  <td className="p-4 text-xs font-medium text-slate-700 dark:text-slate-300 max-w-[250px] truncate" title={t.desc}>
                    {t.desc}
                  </td>
                  <td className="p-4 text-right tabular-nums text-slate-600 dark:text-slate-400 font-medium">
                    {fmtBRL(t.base)}
                  </td>
                  <td className="p-4 text-right tabular-nums font-bold text-amber-600 dark:text-amber-500">
                    {t.aliq.toFixed(2)}%
                  </td>
                  <td className="p-4 text-right tabular-nums font-black text-emerald-600 dark:text-emerald-400">
                    {fmtBRL(t.valor)}
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <div className="w-24 h-1.5 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-1000" 
                          style={{ width: `${Math.min(100, t.pct)}%`, backgroundColor: t.cor }} />
                      </div>
                      <span className="tabular-nums text-[10px] font-bold text-slate-500 dark:text-slate-400 w-8">{t.pct.toFixed(1)}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            {totalTributos > 0 && (
              <tfoot className="bg-slate-50/5 dark:bg-slate-900/50">
                <tr className="border-t border-white/10 font-black">
                  <td colSpan={2} className="p-4 text-sm text-slate-900 dark:text-white uppercase">TOTAL CONSOLIDADO</td>
                  <td className="p-4 text-right tabular-nums text-slate-600 dark:text-slate-400">{fmtBRL(totalBase)}</td>
                  <td className="p-4 text-right tabular-nums text-amber-600 dark:text-amber-500">{aliquotaEfetiva.toFixed(2)}%</td>
                  <td className="p-4 text-right tabular-nums text-lg text-emerald-600 dark:text-emerald-400">{fmtBRL(totalTributos)}</td>
                  <td className="p-4 text-right text-slate-500 dark:text-slate-400 text-xs">100%</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub, icon: Icon, color }: { label: string; value: string; sub: string; icon: any; color: string }) {
  return (
    <div className="glass-card p-5 hover-lift group">
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-lg group-hover:bg-primary group-hover:text-white transition-colors">
          <Icon className="size-4" />
        </div>
        <span className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider">{label}</span>
      </div>
      <div className={`text-2xl font-black tabular-nums dark:text-white ${color}`}>{value}</div>
      <div className="text-[10px] text-muted-foreground mt-1">{sub}</div>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="h-[300px] flex flex-col items-center justify-center text-muted-foreground text-sm gap-2">
      <Info className="size-8 opacity-20" />
      {message}
    </div>
  );
}
